import { useTitleOwnerDocumentPresignedUrlQuery } from '@graphql/index';
import { Box } from '@spark-web/box';
import { DocumentTextIcon, XIcon } from '@spark-web/icon';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';

type TitleOwnerDocumentLinkProps = {
  applicationId: string;
  titleSearchOwnerId: string;
  documentId: string;
  fileName: string;
  canRemove?: boolean;
  // The parent owns deletion so its mutation callbacks remain mounted after this row is removed.
  onRemoveDocument: (documentId: string) => void;
};

// Title-owner analogue of UploadedDocumentsLink: shows an already-uploaded document as a
// download link (presigned GET URL) with an X that deletes it via deleteTitleOwnerDocument.
const TitleOwnerDocumentLink = ({
  applicationId,
  titleSearchOwnerId,
  documentId,
  fileName,
  canRemove = true,
  onRemoveDocument,
}: TitleOwnerDocumentLinkProps) => {
  const { data: downloadUrl } = useTitleOwnerDocumentPresignedUrlQuery(
    { input: { applicationId, titleSearchOwnerId, documentId } },
    {
      enabled: !!documentId,
      select: data => data.titleOwnerDocumentPresignedUrl,
    },
  );
  const handleRemoveDocument = () => {
    if (!canRemove) return;
    onRemoveDocument(documentId);
  };

  if (!downloadUrl) {
    return null;
  }

  return (
    <Box border="field" borderRadius="small" padding="xsmall">
      <Box display="flex" alignItems="center" gap="medium" height="medium" paddingLeft="small">
        <DocumentTextIcon size="xsmall" />
        <Box flex={1}>
          <Text>
            <TextLink href={downloadUrl}>{fileName}</TextLink>
          </Text>
        </Box>
        {canRemove && (
          <Box
            as="button"
            type="button"
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="small"
            height="medium"
            width="medium"
            aria-label={`Remove ${fileName}`}
            onClick={handleRemoveDocument}
          >
            <XIcon size="xxsmall" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TitleOwnerDocumentLink;
